import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Cell, Tooltip } from 'recharts';
import { Megaphone, Users, Plus, Trash2, TrendingUp, CalendarClock, Send, CheckCircle2, Clock, Wand2, Link2, Zap, Instagram, Linkedin, Youtube, Facebook, ShieldCheck, ChevronDown } from 'lucide-react';
import { api } from '@/lib/api';
import { luxe, PILLARS, gradientCss } from '@/lib/luxe';
import { GlassCard, LuxeButton, SectionTitle, Input, Textarea, Field, StatCard, Spinner, Empty } from '@/components/bits';
import MyraMarketing from '@/pages/MyraMarketing';

const P = PILLARS.marketing;
const TABS = ['Myra AI', 'Overview', 'Campaigns', 'Calendar', 'Social', 'Leads CRM'];
const STAGES = ['new', 'contacted', 'qualified', 'proposal', 'won', 'lost'];

export default function Marketing() {
  const [tab, setTab] = useState('Myra AI');
  const [analytics, setAnalytics] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [leads, setLeads] = useState([]);
  const [posts, setPosts] = useState([]);
  const [socials, setSocials] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [a, c, l, p, s] = await Promise.all([
        api('/studio-marketing/analytics').catch(() => null),
        api('/studio-marketing/campaigns').catch(() => ({ items: [] })),
        api('/studio-marketing/leads').catch(() => ({ items: [] })),
        api('/studio-marketing/posts').catch(() => ({ items: [] })),
        api('/studio-marketing/social/connections').catch(() => ({ items: [] })),
      ]);
      setAnalytics(a); setCampaigns(c.items || []); setLeads(l.items || []); setPosts(p.items || []); setSocials(s.items || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, []);

  // Campaign form
  const [cf, setCf] = useState({ name: '', objective: 'awareness', budget_credits: 0 });
  const createCampaign = async () => {
    if (!cf.name.trim()) return toast.error('Campaign name required');
    const today = new Date().toISOString().slice(0, 10);
    const end = new Date(Date.now() + 7 * 864e5).toISOString().slice(0, 10);
    try {
      await api('/studio-marketing/campaigns', { method: 'POST', body: { ...cf, channels: ['instagram'], start_date: today, end_date: end } });
      toast.success('Campaign created'); setCf({ name: '', objective: 'awareness', budget_credits: 0 }); load();
    } catch (e) { toast.error(e.message); }
  };

  // Lead form
  const [lf, setLf] = useState({ name: '', email: '', source: 'instagram', stage: 'new' });
  const createLead = async () => {
    if (!lf.name.trim()) return toast.error('Lead name required');
    try {
      await api('/studio-marketing/leads', { method: 'POST', body: lf });
      toast.success('Lead added'); setLf({ name: '', email: '', source: 'instagram', stage: 'new' }); load();
    } catch (e) { toast.error(e.message); }
  };
  const delLead = async (id) => { await api(`/studio-marketing/leads/${id}`, { method: 'DELETE' }); load(); };
  const delCampaign = async (id) => { await api(`/studio-marketing/campaigns/${id}`, { method: 'DELETE' }); load(); };

  // Post form (Content Calendar)
  const defaultSchedule = () => new Date(Date.now() + 5 * 60000).toISOString().slice(0, 16);
  const [pf, setPf] = useState({ caption: '', channel: 'instagram', scheduled_at: defaultSchedule() });
  const CHANNELS = ['instagram', 'facebook', 'linkedin', 'youtube', 'twitter', 'telegram'];
  const createPost = async () => {
    if (pf.caption.trim().length < 1) return toast.error('Caption required');
    try {
      await api('/studio-marketing/posts', { method: 'POST', body: { caption: pf.caption, channels: [pf.channel], scheduled_at: new Date(pf.scheduled_at).toISOString(), status: 'scheduled' } });
      toast.success('Post scheduled — will auto-publish at the set time');
      setPf({ caption: '', channel: 'instagram', scheduled_at: defaultSchedule() }); load();
    } catch (e) { toast.error(e.message); }
  };
  const delPost = async (id) => { await api(`/studio-marketing/posts/${id}`, { method: 'DELETE' }); load(); };

  // AI caption + hashtag suggest
  const [aiTopic, setAiTopic] = useState('');
  const [aiBusy, setAiBusy] = useState(false);
  const aiSuggest = async () => {
    if (aiTopic.trim().length < 2) return toast.error('Enter a topic for the AI');
    setAiBusy(true);
    try {
      const r = await api('/studio-marketing/ai/suggest', { method: 'POST', body: { topic: aiTopic, channel: pf.channel, tone: 'premium' } });
      const tags = (r.hashtags || []).join(' ');
      setPf({ ...pf, caption: `${r.caption}${tags ? `\n\n${tags}` : ''}` });
      toast.success('AI caption + hashtags added ✨');
    } catch (e) { toast.error(e.message); } finally { setAiBusy(false); }
  };

  // ---- Social connections (Batch 2A: guided / OAuth foreground; manual = advanced) ----
  const PROVIDER_META = {
    telegram: { label: 'Telegram', icon: Send },
    instagram: { label: 'Instagram', icon: Instagram },
    linkedin: { label: 'LinkedIn', icon: Linkedin },
    facebook: { label: 'Facebook', icon: Facebook },
    youtube: { label: 'YouTube', icon: Youtube },
  };
  const [providers, setProviders] = useState([]);
  const [tgInfo, setTgInfo] = useState(null);
  const [tgChat, setTgChat] = useState('');
  const [tgBusy, setTgBusy] = useState(false);
  const [oauthBusy, setOauthBusy] = useState('');
  const [showManual, setShowManual] = useState(false);
  const [scForm, setScForm] = useState({ platform: 'instagram', access_token: '', account_id: '', display_name: '' });

  const loadProviders = async () => {
    const [pv, tg] = await Promise.all([
      api('/social/providers').catch(() => ({ providers: [] })),
      api('/social/telegram/instructions').catch(() => null),
    ]);
    setProviders(pv.providers || []);
    setTgInfo(tg);
  };
  useEffect(() => { loadProviders(); }, []);

  // OAuth return handler (?oauth=success|failed)
  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    const o = q.get('oauth');
    if (!o) return;
    if (o === 'success') toast.success(`${q.get('platform') || 'Account'} connected`);
    else toast.error('Connection was cancelled or failed');
    setTab('Social');
    window.history.replaceState({}, '', window.location.pathname);
    load();
  }, []);

  const connectOAuth = async (platform) => {
    setOauthBusy(platform);
    try {
      const r = await api(`/social/oauth/${platform}/start-ticket`, { method: 'POST' });
      window.location.assign(r.start_url);
    } catch (e) {
      if (e.data?.detail?.error === 'PROVIDER_NOT_CONFIGURED') toast.info(`${PROVIDER_META[platform]?.label || platform} sign-in isn't available yet — coming soon`);
      else toast.error(e.message);
      setOauthBusy('');
    }
  };

  const linkTelegram = async () => {
    if (!tgChat.trim()) return toast.error('Enter your channel @username or chat id');
    setTgBusy(true);
    try {
      const r = await api('/social/telegram/link', { method: 'POST', body: { chat_id: tgChat.trim() } });
      toast.success(`Telegram connected — ${r.account?.title || tgChat}`);
      setTgChat(''); load();
    } catch (e) {
      if (e.data?.detail?.error === 'TELEGRAM_NOT_CONFIGURED') toast.info("Telegram isn't set up by the workspace admin yet");
      else toast.error(e.message);
    } finally { setTgBusy(false); }
  };

  const connectSocial = async () => {
    if (scForm.access_token.trim().length < 8) return toast.error('Enter a valid access token');
    try {
      await api('/studio-marketing/social/connect', { method: 'POST', body: scForm });
      toast.success(`${scForm.platform} connected (manual)`);
      setScForm({ ...scForm, access_token: '', account_id: '', display_name: '' }); load();
    } catch (e) { toast.error(e.message); }
  };
  const disconnectSocial = async (platform) => { await api(`/studio-marketing/social/${platform}`, { method: 'DELETE' }); load(); };

  const k = analytics?.kpi || {};

  return (
    <div>
      <SectionTitle eyebrow="Pillar 2 · Marketing AI" title="Marketing Dashboard" color={P.primary}
        desc="Launch campaigns, track leads through your funnel, and watch ROI update in real time." />

      <div className="flex gap-2 mb-6 flex-wrap">
        {TABS.map((t) => (
          <button key={t} onClick={() => setTab(t)} data-testid={`mkt-tab-${t}`}
            className="px-4 py-2 rounded-full text-sm font-bold luxe-btn"
            style={tab === t
              ? { background: gradientCss(P.grad), color: '#3A2C05', boxShadow: `0 8px 20px ${P.glow}` }
              : { background: luxe.surface, color: luxe.textMid, border: `1px solid ${luxe.border}` }}>
            {t}
          </button>
        ))}
      </div>

      {tab === 'Myra AI' && <MyraMarketing />}

      {tab !== 'Myra AI' && (loading ? <div className="flex justify-center py-16"><Spinner size={28} /></div> : (
        <>
          {tab === 'Overview' && (
            <div className="float-in">
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                <StatCard label="Impressions" value={(k.impressions || 0).toLocaleString()} accent={P.primary} />
                <StatCard label="Conversions" value={k.conversions || 0} sub={`${k.conversion_rate_pct || 0}% rate`} accent={luxe.blue} />
                <StatCard label="Revenue" value={`₹${(k.revenue_inr || 0).toLocaleString()}`} accent={luxe.success} />
                <StatCard label="ROI" value={`${k.roi_pct || 0}%`} accent={luxe.gold} />
              </div>
              <GlassCard className="p-6">
                <div className="flex items-center gap-2 mb-4"><TrendingUp size={18} style={{ color: P.primary }} /><span className="font-bold" style={{ color: luxe.text }}>Lead Funnel</span></div>
                {leads.length === 0 ? <Empty icon={Users} title="No leads yet" desc="Add leads in the CRM tab to see your funnel." /> : (
                  <ResponsiveContainer width="100%" height={260}>
                    <BarChart data={analytics?.funnel || []}>
                      <XAxis dataKey="stage" tick={{ fill: luxe.muted, fontSize: 12 }} axisLine={{ stroke: luxe.border }} tickLine={false} />
                      <YAxis tick={{ fill: luxe.muted, fontSize: 12 }} axisLine={false} tickLine={false} allowDecimals={false} />
                      <Tooltip contentStyle={{ background: '#12142B', border: `1px solid ${luxe.border}`, borderRadius: 12 }} cursor={{ fill: 'rgba(37,99,235,0.06)' }} />
                      <Bar dataKey="count" radius={[8, 8, 0, 0]}>
                        {(analytics?.funnel || []).map((_, i) => <Cell key={i} fill={i % 2 ? P.primary : luxe.blue} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </GlassCard>
            </div>
          )}

          {tab === 'Campaigns' && (
            <div className="float-in">
              <GlassCard className="p-5 mb-5">
                <div className="grid sm:grid-cols-3 gap-3 items-end">
                  <Field label="Campaign Name"><Input value={cf.name} onChange={(e) => setCf({ ...cf, name: e.target.value })} placeholder="Diwali Launch" data-testid="campaign-name" /></Field>
                  <Field label="Objective">
                    <select value={cf.objective} onChange={(e) => setCf({ ...cf, objective: e.target.value })} className="w-full outline-none" style={{ padding: '12px 14px', borderRadius: 12, border: `1px solid ${luxe.border}`, background: '#0B0D1E', color: luxe.text }}>
                      {['awareness', 'engagement', 'leads', 'sales', 'app_installs'].map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </Field>
                  <Field label="Budget (credits)"><Input type="number" value={cf.budget_credits} onChange={(e) => setCf({ ...cf, budget_credits: +e.target.value })} /></Field>
                </div>
                <LuxeButton variant="pillar" color={P} onClick={createCampaign} data-testid="create-campaign"><Plus size={16} className="inline mr-1" />Create Campaign</LuxeButton>
              </GlassCard>
              {campaigns.length === 0 ? <Empty icon={Megaphone} title="No campaigns yet" /> : (
                <div className="space-y-3">
                  {campaigns.map((c) => (
                    <GlassCard key={c.id} className="p-4 flex items-center justify-between">
                      <div>
                        <div className="font-bold" style={{ color: luxe.text }}>{c.name}</div>
                        <div className="text-xs mt-0.5" style={{ color: luxe.muted }}>{c.objective} · {c.status} · {c.budget_credits} credits</div>
                      </div>
                      <button onClick={() => delCampaign(c.id)} style={{ color: luxe.danger }}><Trash2 size={16} /></button>
                    </GlassCard>
                  ))}
                </div>
              )}
            </div>
          )}

          {tab === 'Calendar' && (
            <div className="float-in">
              <GlassCard className="p-5 mb-5">
                <div className="flex items-center gap-2 mb-3"><CalendarClock size={18} style={{ color: P.primary }} /><span className="font-bold" style={{ color: luxe.text }}>Schedule a Post</span></div>
                <Field label="Caption"><Textarea rows={3} value={pf.caption} onChange={(e) => setPf({ ...pf, caption: e.target.value })} placeholder="Write your post caption…" data-testid="post-caption" /></Field>
                <div className="p-3 rounded-xl mb-4" style={{ background: 'rgba(236,72,153,0.08)', border: `1px solid rgba(236,72,153,0.25)` }}>
                  <div className="text-[10px] font-extrabold uppercase tracking-widest mb-2 pink-text">AI Caption + Hashtags</div>
                  <div className="flex gap-2">
                    <Input value={aiTopic} onChange={(e) => setAiTopic(e.target.value)} placeholder="Topic e.g. Diwali coffee sale" data-testid="ai-topic" />
                    <LuxeButton variant="pillar" color={PILLARS.marketing} onClick={aiSuggest} disabled={aiBusy} data-testid="ai-suggest">
                      {aiBusy ? <Spinner color="#fff" /> : <><Wand2 size={15} className="inline mr-1" />Suggest</>}
                    </LuxeButton>
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-3">
                  <Field label="Channel">
                    <select value={pf.channel} onChange={(e) => setPf({ ...pf, channel: e.target.value })} className="w-full outline-none" style={{ padding: '12px 14px', borderRadius: 12, border: `1px solid ${luxe.border}`, background: '#0B0D1E', color: luxe.text }}>
                      {CHANNELS.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </Field>
                  <Field label="Publish At"><Input type="datetime-local" value={pf.scheduled_at} onChange={(e) => setPf({ ...pf, scheduled_at: e.target.value })} data-testid="post-schedule" /></Field>
                </div>
                <LuxeButton variant="pillar" color={P} onClick={createPost} data-testid="create-post"><Send size={16} className="inline mr-1" />Schedule Post</LuxeButton>
                <div className="text-xs mt-3" style={{ color: luxe.muted }}>Posts auto-publish at their scheduled time (status flips to <b style={{ color: luxe.success }}>published</b> automatically).</div>
              </GlassCard>
              {posts.length === 0 ? <Empty icon={CalendarClock} title="No posts scheduled" /> : (
                <div className="space-y-2">
                  {posts.map((p) => {
                    const done = p.status === 'published';
                    return (
                      <GlassCard key={p.id} className="p-4 flex items-center justify-between gap-3">
                        <div className="min-w-0">
                          <div className="font-semibold text-sm truncate" style={{ color: luxe.text }}>{p.caption}</div>
                          <div className="text-xs mt-0.5 flex items-center gap-1.5" style={{ color: luxe.muted }}>
                            {(p.channels || []).join(', ')} · {new Date(p.scheduled_at).toLocaleString()}
                          </div>
                        </div>
                        <div className="flex items-center gap-3 shrink-0">
                          <span className="flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold" style={{ background: done ? 'rgba(14,169,114,0.12)' : 'rgba(245,183,10,0.14)', color: done ? luxe.success : luxe.warning, border: `1px solid ${luxe.border}` }}>
                            {done ? <CheckCircle2 size={13} /> : <Clock size={13} />} {p.status}{done ? (p.was_live ? ' · live' : ' · sim') : ''}
                          </span>
                          <button onClick={() => delPost(p.id)} style={{ color: luxe.danger }}><Trash2 size={16} /></button>
                        </div>
                      </GlassCard>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {tab === 'Social' && (
            <div className="float-in space-y-5" data-testid="social-tab">
              {/* Telegram guided connection */}
              <GlassCard className="p-5">
                <div className="flex items-center gap-2 mb-1"><Send size={18} style={{ color: P.primary }} /><span className="font-bold" style={{ color: luxe.text }}>Connect Telegram</span>
                  {tgInfo && !tgInfo.configured && <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: luxe.surface, color: luxe.muted, border: `1px solid ${luxe.border}` }}>admin setup needed</span>}
                </div>
                <p className="text-sm mb-3" style={{ color: luxe.muted }}>Add the Myra bot to your channel, then verify — no bot token needed from you.</p>
                {tgInfo?.steps && (
                  <ol className="text-xs mb-3 space-y-1 list-decimal pl-5" style={{ color: luxe.muted }}>
                    {tgInfo.steps.map((s, i) => <li key={i}>{s}</li>)}
                  </ol>
                )}
                <div className="grid sm:grid-cols-[1fr_auto] gap-3 items-end">
                  <Field label="Channel @username or chat id"><Input value={tgChat} onChange={(e) => setTgChat(e.target.value)} placeholder="@mychannel" data-testid="telegram-chat-input" /></Field>
                  <LuxeButton variant="pillar" color={P} onClick={linkTelegram} disabled={tgBusy} data-testid="telegram-connect-btn"><ShieldCheck size={15} className="inline mr-1" />{tgBusy ? 'Verifying…' : 'Verify & Connect'}</LuxeButton>
                </div>
              </GlassCard>

              {/* OAuth providers */}
              <GlassCard className="p-5">
                <div className="flex items-center gap-2 mb-1"><Zap size={18} style={{ color: P.primary }} /><span className="font-bold" style={{ color: luxe.text }}>Connect with official sign-in</span></div>
                <p className="text-sm mb-4" style={{ color: luxe.muted }}>Securely authorize your accounts — you'll be redirected to the platform's official login. We never see your password or tokens.</p>
                <div className="grid sm:grid-cols-2 gap-3">
                  {providers.filter((p) => p.connect_kind === 'oauth').map((p) => {
                    const Ic = (PROVIDER_META[p.id] || {}).icon || Link2;
                    return (
                      <div key={p.id} className="flex items-center justify-between p-3 rounded-xl" style={{ background: luxe.surface, border: `1px solid ${luxe.border}` }} data-testid={`oauth-provider-${p.id}`}>
                        <div className="flex items-center gap-2"><Ic size={18} style={{ color: luxe.text }} /><span className="font-bold" style={{ color: luxe.text }}>{p.label}</span>
                          {!p.configured && <span className="text-xs" style={{ color: luxe.muted }}>coming soon</span>}
                        </div>
                        <LuxeButton variant="ghost" color={P} onClick={() => connectOAuth(p.id)} disabled={!p.configured || oauthBusy === p.id} data-testid={`oauth-connect-${p.id}`}>
                          <Link2 size={14} className="inline mr-1" />{oauthBusy === p.id ? 'Opening…' : 'Connect'}
                        </LuxeButton>
                      </div>
                    );
                  })}
                </div>
              </GlassCard>

              {/* Connected accounts */}
              <div>
                <div className="text-sm font-bold mb-2" style={{ color: luxe.text }}>Connected accounts</div>
                {socials.length === 0 ? <Empty icon={Link2} title="No accounts connected" desc="Connect Telegram or an official account above." /> : (
                  <div className="space-y-2">
                    {socials.map((s) => (
                      <GlassCard key={s.platform} className="p-4 flex items-center justify-between" data-testid={`connection-${s.platform}`}>
                        <div className="flex items-center gap-2">
                          <CheckCircle2 size={16} style={{ color: luxe.success }} />
                          <div>
                            <div className="font-bold capitalize" style={{ color: luxe.text }}>{s.platform}</div>
                            <div className="text-xs" style={{ color: luxe.muted }}>{s.display_name || s.account_id || 'connected'} · {s.status || 'connected'}</div>
                          </div>
                        </div>
                        <button onClick={() => disconnectSocial(s.platform)} style={{ color: luxe.danger }} data-testid={`disconnect-${s.platform}`}><Trash2 size={16} /></button>
                      </GlassCard>
                    ))}
                  </div>
                )}
              </div>

              {/* Advanced: manual token (developers only) */}
              <div>
                <button onClick={() => setShowManual((v) => !v)} className="text-xs flex items-center gap-1" style={{ color: luxe.muted }} data-testid="toggle-manual-connect">
                  <ChevronDown size={13} className="inline" style={{ transform: showManual ? 'rotate(180deg)' : 'none', transition: 'transform .2s' }} />Advanced: connect with a manual token (developers)
                </button>
                {showManual && (
                  <GlassCard className="p-5 mt-2">
                    <p className="text-xs mb-3" style={{ color: luxe.muted }}>For development/testing only. Most users should use the guided or official sign-in above.</p>
                    <div className="flex flex-wrap gap-2 mb-3">
                      {['instagram', 'linkedin', 'telegram'].map((pid) => (
                        <button key={pid} onClick={() => setScForm({ ...scForm, platform: pid })} className="px-3 py-2 rounded-full text-sm font-bold luxe-btn"
                          style={scForm.platform === pid ? { background: gradientCss(P.grad), color: '#2A2005' } : { background: luxe.surface, color: luxe.textMid, border: `1px solid ${luxe.border}` }}>
                          {(PROVIDER_META[pid] || {}).label || pid}
                        </button>
                      ))}
                    </div>
                    <div className="grid sm:grid-cols-2 gap-3">
                      <Field label="Access Token / Bot Token"><Input type="password" value={scForm.access_token} onChange={(e) => setScForm({ ...scForm, access_token: e.target.value })} placeholder="paste token" data-testid="social-token" /></Field>
                      <Field label="Account ID / Chat ID / URN"><Input value={scForm.account_id} onChange={(e) => setScForm({ ...scForm, account_id: e.target.value })} placeholder="e.g. @mychannel or urn:li:person:xxx" data-testid="social-accountid" /></Field>
                    </div>
                    <LuxeButton variant="pillar" color={P} onClick={connectSocial} data-testid="social-connect"><Link2 size={15} className="inline mr-1" />Connect (manual)</LuxeButton>
                  </GlassCard>
                )}
              </div>
            </div>
          )}

          {tab === 'Leads CRM' && (
            <div className="float-in">
              <GlassCard className="p-5 mb-5">
                <div className="grid sm:grid-cols-4 gap-3 items-end">
                  <Field label="Name"><Input value={lf.name} onChange={(e) => setLf({ ...lf, name: e.target.value })} data-testid="lead-name" /></Field>
                  <Field label="Email"><Input value={lf.email} onChange={(e) => setLf({ ...lf, email: e.target.value })} /></Field>
                  <Field label="Source"><Input value={lf.source} onChange={(e) => setLf({ ...lf, source: e.target.value })} /></Field>
                  <Field label="Stage">
                    <select value={lf.stage} onChange={(e) => setLf({ ...lf, stage: e.target.value })} className="w-full outline-none" style={{ padding: '12px 14px', borderRadius: 12, border: `1px solid ${luxe.border}`, background: '#0B0D1E', color: luxe.text }}>
                      {STAGES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </Field>
                </div>
                <LuxeButton variant="pillar" color={P} onClick={createLead} data-testid="create-lead"><Plus size={16} className="inline mr-1" />Add Lead</LuxeButton>
              </GlassCard>
              {leads.length === 0 ? <Empty icon={Users} title="No leads yet" /> : (
                <div className="space-y-2">
                  {leads.map((l) => (
                    <GlassCard key={l.id} className="p-4 flex items-center justify-between">
                      <div>
                        <div className="font-bold" style={{ color: luxe.text }}>{l.name}</div>
                        <div className="text-xs mt-0.5" style={{ color: luxe.muted }}>{l.email || 'no email'} · {l.source}</div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="px-3 py-1 rounded-full text-xs font-bold" style={{ background: l.stage === 'won' ? 'rgba(14,169,114,0.12)' : luxe.surface2, color: l.stage === 'won' ? luxe.success : luxe.textMid, border: `1px solid ${luxe.border}` }}>{l.stage}</span>
                        <button onClick={() => delLead(l.id)} style={{ color: luxe.danger }}><Trash2 size={16} /></button>
                      </div>
                    </GlassCard>
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      ))}
    </div>
  );
}
